<script setup>
import {useUserStore} from '@/plugins/store'
import {computed, inject, onMounted, watch} from 'vue'
import NotificationMenu from '@/components/menu/NotificationMenu.vue'
import UserMenu from '@/components/menu/UserMenu.vue'
import * as Nav from '@/service/nav'
import {isHalloween, isChristmas, anonEmoji, HIDE_GUIDES} from '@/plugins/constants'
import {useLocale} from 'vuetify'
import {semesterApi, userApi} from "@/service/api";
import { useRoute, useRouter } from 'vue-router'
const router = useRouter()


const revertImpersonation = async () => {
  try {
    await userApi.revertImpersonation()

    userStore.clearImpersonation()
    router.go(0)

    appState.value.notifications.push({
      type: 'success',
      title: t('$vuetify.impersonation_mode_out'),
      text: t('$vuetify.impersonation_activated_text_prefix') +
          ` ${userStore.user.name} (${userStore.user.username}).`
    })
  } catch (e) {
    appState.value.notifications.push({
      type: 'error',
      title: t('$vuetify.impersonation_reverted_prefix'),
      text: e?.response?.data?.message || t('$vuetify.impersonation_error_fallback')
    })
  }
}


const appState = inject('appState')
const userStore = useUserStore()
const { t, current } = useLocale()
const store = useUserStore()

const anonymizeName = (item) => (item.anonymous && userStore.anonymous ? anonEmoji(item.anonymous) : t(item.name))
const callback = (state) => {
  document.title = 'Trainer' + (state.navigation.length > 1 ? ` | ${anonymizeName(state.navigation.slice(-1)[0])}` : '')
}

const toggleLocale = () => {
  userStore.toggleLocale()
  current.value = userStore.locale
}

const route = useRoute()

const shouldShowAppBar = computed(() => {
  const path = route.path
  return !(
      path === '/info/cs' ||
      path === '/info/en'
  )
})

onMounted(async () => {
  if (store.isLoggedIn)
    semesterApi.listSemesters()
        .then((result) => {
          if (userStore.isGuarantor)
            userStore.semester = result[0].id
        })
})

watch(appState, callback, {deep: true})
</script>

<template>
  <v-app-bar
    v-if="shouldShowAppBar"
    :color="userStore.isImpersonated
      ? '#ff2743'
      : (isHalloween 
        ? 'orange' 
        : (isChristmas 
          ? 'green-darken-4' 
          : (store.isLoggedIn && userStore.isAdmin 
            ? '#01013F' 
            : (store.isLoggedIn && userStore.isAnyTeacher 
              ? 'purple' 
              : 'indigo'
            )
          )
        )
      )"
    prominent>
    <v-btn v-if="appState.leftDrawer !== undefined" icon="mdi-menu"
           @click="appState.leftDrawer = !appState.leftDrawer" />
    <v-btn icon="mdi-timer-outline" :to="{name: 'course-list'}" :active="false" />

    <div v-if="isChristmas && appState.seasonalFxEnabled" class="xmas-decor" aria-hidden="true" />

    <v-toolbar-title>
      <template v-for="(item, index) in appState.navigation" :key="item.name">
        <router-link v-if="index !== appState.navigation.length - 1"
                     class="text-decoration-none"
                     :to="{name: item.path, params: item.params}" :style="`color: ${isHalloween ? 'black' : 'white'}`">
          {{ anonymizeName(item) + ' > ' }}
        </router-link>
        <span v-else class="text-decoration-none">
          {{ anonymizeName(item) }}
        </span>
      </template>
    </v-toolbar-title>

    <template v-if="store.isLoggedIn">
      <v-menu v-if="userStore.isAdmin || userStore.isGuarantor" open-on-hover>
        <template #activator="{ props }">
          <v-btn color="white" v-bind="props" dark>
            <span v-if="userStore.isAdmin">{{ t('$vuetify.user_menu_admin_button') }}</span>
            <span v-else-if="userStore.isGuarantor">{{ t('$vuetify.user_menu_guarantor_button') }}</span>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-if="userStore.isAdmin" :to="{name: 'semester-list'}">
            <v-list-item-title>
              {{ t('$vuetify.semester_title') }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="userStore.isAdmin || userStore.isGuarantor" :to="new Nav.SubjectsList(userStore.semester).routerPath()">
            <v-list-item-title>
              {{ t('$vuetify.subject_list') }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="userStore.isAdmin || userStore.isGuarantor" :to="{name: 'topic-list'}">
            <v-list-item-title>
              {{ t("$vuetify.topic_list_title") }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <div
        v-if="userStore.isImpersonated"
        class="d-flex align-center mr-4"
        style="gap: 16px;"
      >
        <span class="text-body-1 font-weight-medium text-white">
          {{ t('$vuetify.impersonation_mode') }}
        </span>

        <v-btn
          variant="outlined"
          color="white"
          prepend-icon="mdi-logout-variant"
          height="36"
          @click="revertImpersonation"
        >
          {{ t('$vuetify.impersonation_mode_off') }}
        </v-btn>
      </div>


      <NotificationMenu />

      <v-btn v-if="!HIDE_GUIDES" :to="{name: 'guide'}" icon>
        <v-icon icon="mdi-help" />
        <v-tooltip activator="parent" location="bottom">
          {{ t('$vuetify.guides') }}
        </v-tooltip>
      </v-btn>

      <v-btn v-if="isChristmas"
             icon @click="appState.seasonalFxEnabled = !appState.seasonalFxEnabled">
        <v-icon icon="mdi-weather-snowy" />
        <v-tooltip activator="parent" location="bottom">
          {{ appState.seasonalFxEnabled ? 'Disable Christmas effects' : 'Enable Christmas effects' }}
        </v-tooltip>
      </v-btn>

      <UserMenu :callback="callback" :is-halloween="isHalloween" />
    </template>
    <template v-else>
      <v-btn class="mx-2" @click="toggleLocale">
        {{ t('$vuetify.user_menu_switch_language') }}
      </v-btn>
      <v-btn :to="{name: 'login'}" class="mr-4">
        {{ t('$vuetify.login') }}
      </v-btn>
    </template>
  </v-app-bar>
</template>
<style scoped>
.xmas-decor {
  position: absolute;
  left: 0; right: 0;
  top: 0;
  height: 24px;
  pointer-events: none;
  z-index: -1;
  opacity: 0.8;

  background:
      linear-gradient(to right, rgba(0,0,0,0.25), rgba(0,0,0,0.25)) 0 2px/100% 2px no-repeat,
      radial-gradient(circle at 10px 12px, rgba(255,80,80,0.95) 0, rgba(255,80,80,0.5) 5px, transparent 6px) 0 0/48px 24px repeat-x,
      radial-gradient(circle at 34px 12px, rgba(120,255,120,0.95) 0, rgba(120,255,120,0.5) 5px, transparent 6px) 0 0/48px 24px repeat-x,
      radial-gradient(circle at 22px 16px, rgba(255,230,120,0.95) 0, rgba(255,230,120,0.5) 5px, transparent 6px) 0 0/48px 24px repeat-x;

  animation: xmas-twinkle 1s ease-in-out infinite alternate;
  filter: drop-shadow(0 1px 1px rgba(0,0,0,0.4));
}

@keyframes xmas-twinkle {
  0% { opacity: 0.6; }
  100% { opacity: 0.9; }
}
</style>
